import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { BACKEND_ORIGIN } from "@/lib/api";
import Layout from "@/components/Layout";
import { useI18n } from "@/i18n/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Loader2, Save, Upload, ImageIcon, Copy, Check, Zap, ZapOff, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import CarteMotDePasse from "@/components/CarteMotDePasse";
import CarteParrainage from "@/components/CarteParrainage";
import AlertePrix from "@/components/AlertePrix";
import { paireLangues, prixConfigures } from "@/lib/roof";

/**
 * Montants d'EXEMPLE. Un compte neuf n'a plus aucun prix pré-rempli (voir
 * `default_settings()` côté serveur) : ces valeurs ne servent qu'à afficher un
 * placeholder grisé montrant le format attendu, et ne sont JAMAIS enregistrées.
 * Un prix suggéré finit toujours par se retrouver tel quel dans une vraie
 * soumission — les chiffres doivent venir du couvreur.
 */
const EX = {
  taxe: "8",
  mo: "165",
  moPlat: "185",
  materiaux: ["22", "18", "14", "9"],
  produits: ["115", "165", "210", "160", "175", "210"],
};
/** Un montant à zéro est un montant non saisi : on laisse voir l'exemple. */
const ex0 = (v) => (Number(v) ? v : "");
/** Exemple à afficher pour la n-ième ligne d'une liste, sinon un montant neutre. */
const exN = (liste, i) => liste[i] || "25";

/**
 * À quel type de toit s'applique un poste. « Tous » reste la valeur par défaut :
 * un compte qui n'a jamais touché à ce réglage facture comme avant.
 */
function SelectToit({ valeur, onChange, fr, testId }) {
  return (
    <select
      data-testid={testId}
      value={valeur || "tous"}
      onChange={(e) => onChange(e.target.value)}
      title={fr ? "Type de toit où ce poste s'applique" : "Roof type this item applies to"}
      className="bg-field border border-line-strong rounded-md px-2 py-2 text-xs text-ink-soft focus:border-orange-500 outline-none shrink-0"
    >
      <option value="tous">{fr ? "Tous" : "All"}</option>
      <option value="pente">{fr ? "En pente" : "Sloped"}</option>
      <option value="plat">{fr ? "Plat" : "Flat"}</option>
    </select>
  );
}

// Palette d'accents proposée au couvreur. Une pastille suffit dans la plupart
// des cas ; le sélecteur libre reste là pour coller à une charte précise.
const COULEURS_WIDGET = [
  { hex: "#EA580C", fr: "Orange", en: "Orange" },
  { hex: "#DC2626", fr: "Rouge", en: "Red" },
  { hex: "#CA8A04", fr: "Or", en: "Gold" },
  { hex: "#059669", fr: "Vert", en: "Green" },
  { hex: "#0D9488", fr: "Sarcelle", en: "Teal" },
  { hex: "#2563EB", fr: "Bleu", en: "Blue" },
  { hex: "#1E3A8A", fr: "Bleu nuit", en: "Navy" },
  { hex: "#7C3AED", fr: "Violet", en: "Purple" },
  { hex: "#334155", fr: "Ardoise", en: "Slate" },
  { hex: "#171717", fr: "Noir", en: "Black" },
];

/** Mêmes valeurs que la fonction `palette()` du widget, pour un aperçu fidèle. */
function paletteWidget(sombre) {
  return sombre
    ? { fond: "#141414", texte: "#F5F5F5", doux: "#A3A3A3", bord: "#2E2E2E", champ: "#1E1E1E", bordChamp: "#3A3A3A", bloc: "#1C1C1C" }
    : { fond: "#FFFFFF", texte: "#171717", doux: "#737373", bord: "#E5E5E5", champ: "#FFFFFF", bordChamp: "#D4D4D4", bloc: "#FAFAFA" };
}

/**
 * Modèles de suivi montrés au couvreur, avec les balises apparentes.
 * Le serveur envoie exactement ces textes quand le champ est laissé vide ; ils
 * sont reproduits ici pour que le couvreur voie ce qui partira en son nom.
 * `langue` : "fr", "en" ou "es" — les trois langues servies par le produit.
 */
const MODELES_RELANCE = (langue) => (langue === "fr" ? [
  `Bonjour {nom},

Vous avez récemment demandé une estimation pour la toiture du {adresse}. Nous voulions nous assurer que vous l'avez bien reçue, et savoir si des questions vous sont venues depuis.

Si vous le souhaitez, nous pouvons vous rencontrer pour choisir vos bardeaux ensemble : vous verrez les couleurs et les différentes gammes en vrai, et nous répondrons à toutes vos questions sur les travaux, les délais et la garantie. C'est sans engagement de votre part.

Répondez simplement à ce courriel ou appelez-nous au {telephone} et nous fixerons un moment qui vous convient.

{entreprise}`,
  `Bonjour {nom},

Nous revenons vers vous au sujet de votre projet de toiture au {adresse}.

Quinze minutes suffisent pour faire le tour : le choix des matériaux, le déroulement du chantier et ce que couvre la garantie. Nous pouvons passer chez vous ou en discuter au téléphone, selon ce qui vous arrange.

Dites-nous simplement vos disponibilités ou appelez-nous au {telephone}.

{entreprise}`,
  `Bonjour {nom},

Choisir un couvreur n'est pas une décision qu'on prend à la légère, et c'est normal de vouloir comparer.

Si quelque chose reste flou — le prix, les délais, les matériaux, les modalités de paiement — posez-nous la question franchement. Nous préférons une réponse claire à un client qui hésite.

{entreprise}`,
  `Bonjour {nom},

Nous n'avons pas eu de vos nouvelles concernant la toiture du {adresse}. Si le projet est reporté, ce n'est pas un problème : dites-le-nous et nous reprendrons contact au bon moment.

Et si vous avez retenu quelqu'un d'autre, faites-nous signe — nous cesserons de vous écrire.

{entreprise}`,
  `Bonjour {nom},

C'est notre dernier message au sujet de votre estimation de toiture : nous ne voulons pas encombrer votre boîte de réception.

Votre estimation reste valable et notre porte reste ouverte. Écrivez-nous ou appelez-nous au {telephone} quand vous serez prêt, nous reprendrons là où nous en étions.

{entreprise}`,
] : langue !== "es" ? [
  `Hello {nom},

You recently requested an estimate for the roof at {adresse}. We wanted to make sure it reached you, and to ask whether any questions have come up since.

If you'd like, we can meet to pick your shingles together: you'll see the colors and the different product lines in person, and we'll answer every question you have about the work, the schedule and the warranty. No commitment on your side.

Just reply to this email or call us at {telephone} and we'll set a time that works for you.

{entreprise}`,
  `Hello {nom},

We're following up about your roofing project at {adresse}.

Fifteen minutes is enough to cover it all: material options, how the job runs, and what the warranty covers. We can stop by or talk it over the phone, whichever suits you.

Just let us know when you're available, or call us at {telephone}.

{entreprise}`,
  `Hello {nom},

Choosing a roofer isn't a decision to rush, and wanting to compare is only sensible.

If anything is still unclear — the price, the schedule, the materials, the payment terms — ask us straight out. We'd rather give you a clear answer than leave you guessing.

{entreprise}`,
  `Hello {nom},

We haven't heard back about the roof at {adresse}. If the project is postponed, that's no problem: tell us and we'll get back in touch at the right time.

And if you've chosen someone else, just say so — we'll stop writing.

{entreprise}`,
  `Hello {nom},

This is our last message about your roofing estimate: we don't want to clutter your inbox.

Your estimate still stands and our door stays open. Write to us or call {telephone} whenever you're ready and we'll pick up where we left off.

{entreprise}`,
] : [
  `Hola {nom},

Hace poco solicitó un presupuesto para el techo de {adresse}. Queríamos asegurarnos de que lo recibió bien, y saber si le han surgido preguntas desde entonces.

Si lo desea, podemos reunirnos para elegir sus tejas juntos: verá los colores y las distintas gamas en persona, y responderemos a todas sus preguntas sobre los trabajos, los plazos y la garantía. Sin ningún compromiso de su parte.

Simplemente responda a este correo o llámenos al {telephone} y fijaremos un momento que le convenga.

{entreprise}`,
  `Hola {nom},

Le escribimos de nuevo sobre su proyecto de techo en {adresse}.

Quince minutos bastan para repasarlo todo: las opciones de materiales, cómo se desarrolla la obra y qué cubre la garantía. Podemos pasar por su casa o hablarlo por teléfono, como le resulte más cómodo.

Díganos simplemente cuándo está disponible, o llámenos al {telephone}.

{entreprise}`,
  `Hola {nom},

Elegir a un techador no es una decisión que se tome a la ligera, y es normal querer comparar.

Si algo sigue sin estar claro — el precio, los plazos, los materiales, las condiciones de pago — pregúntenos con toda confianza. Preferimos darle una respuesta clara antes que dejarlo con dudas.

{entreprise}`,
  `Hola {nom},

No hemos tenido noticias suyas sobre el techo de {adresse}. Si el proyecto se ha pospuesto, no hay ningún problema: díganoslo y retomaremos el contacto en el momento oportuno.

Y si ha elegido a otra empresa, háganoslo saber — dejaremos de escribirle.

{entreprise}`,
  `Hola {nom},

Este es nuestro último mensaje sobre su presupuesto de techo: no queremos llenar su bandeja de entrada.

Su presupuesto sigue vigente y nuestra puerta sigue abierta. Escríbanos o llámenos al {telephone} cuando esté listo, y retomaremos donde lo dejamos.

{entreprise}`,
]);

const ETAPE_VIDE = {
  delai_jours: 7,
  objet_fr: "", message_fr: "", objet_en: "", message_en: "", objet_es: "", message_es: "",
  objet: "", message: "",
  active: true,
};

/**
 * Champ précédé d'une pastille de langue bien visible : dans les listes
 * bilingues, plus aucun doute sur quelle case correspond à quelle langue.
 */
function ChampLangue({ langue, className = "", ...props }) {
  return (
    // `basis-full` sous 640 px : dans les rangées de prix (nom + type de toit
    // + deux montants + poubelle), le nom se faisait écraser à zéro sur
    // téléphone. Il prend maintenant sa propre ligne ; les rangées parentes
    // sont en flex-wrap pour l'accueillir.
    <div className="relative flex-1 basis-full sm:basis-auto min-w-0">
      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] font-extrabold tracking-wider text-ink-faint bg-hover border border-line rounded px-1.5 py-0.5 pointer-events-none select-none">
        {langue}
      </span>
      <Input {...props} className={`pl-11 ${className}`} />
    </div>
  );
}

/** Libellés des langues proposées dans les bascules et les sélecteurs. */
const NOMS_LANGUES = { fr: "Français", en: "English", es: "Español" };

/**
 * Interrupteur de la langue en cours d'édition, un par bloc bilingue :
 * un seul champ par élément à l'écran, la bascule choisit lequel des deux
 * textes de la paire du compte (FR/EN au Canada, EN/ES aux États-Unis)
 * on est en train d'écrire.
 */
function BasculeLangue({ valeur, onChange, fr, testId, langues = ["fr", "en"] }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-ink-faint hidden sm:inline">{fr ? "Vous éditez :" : "Editing:"}</span>
      <div className="inline-flex rounded-md border border-line-strong overflow-hidden" data-testid={testId}>
        {langues.map((code) => [code, NOMS_LANGUES[code] || code]).map(([code, nom]) => (
          <button
            key={code}
            type="button"
            onClick={() => onChange(code)}
            className={`px-3 py-1.5 text-xs font-bold transition-colors ${
              valeur === code ? "bg-orange-600 text-white" : "bg-field text-ink-soft hover:bg-hover"
            }`}
          >
            {nom}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Liste de couleurs affichable dans un champ texte, quel que soit son format stocké. */
const texteCouleurs = (v) => (Array.isArray(v) ? v.join(", ") : v || "");
const listeCouleurs = (v) =>
  Array.isArray(v) ? v : String(v || "").split(",").map((c) => c.trim()).filter(Boolean);

export default function Settings() {
  const { t, lang } = useI18n();
  const fr = lang === "fr";
  const [settings, setSettings] = useState(null);
  const [abo, setAbo] = useState(null);
  const [saving, setSaving] = useState(false);
  const [copie, setCopie] = useState(false);
  // Langue du code d'intégration du widget. "" = langue du compte
  // (quote_language) : réglage d'intégration ponctuel, jamais persisté en base.
  const [langueWidget, setLangueWidget] = useState("");
  // La rubrique vient de l'URL (/settings/entreprise, /settings/prix, …) :
  // chaque rubrique a son entrée directe dans le menu de gauche.
  const RUBRIQUES = ["entreprise", "prix", "soumission", "widget", "relance", "abonnement"];
  const { onglet: brut } = useParams();
  const onglet = RUBRIQUES.includes(brut) ? brut : "entreprise";

  const [plansDispo, setPlansDispo] = useState([]);
  // Recharge du solde À la Carte : montant libre, borné par le serveur.
  const [montantRecharge, setMontantRecharge] = useState(25);
  const [rechargeEnCours, setRechargeEnCours] = useState(false);

  const recharger = async () => {
    setRechargeEnCours(true);
    try {
      const { data } = await api.post("/stripe/recharge", { montant: Number(montantRecharge) });
      if (data?.url) window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail)
        || (fr ? "Recharge impossible" : "Top-up failed"));
      setRechargeEnCours(false);
    }
  };
  const [intervalle, setIntervalle] = useState("month");
  // Langue en cours d'édition, indépendante pour chacun des deux blocs
  // bilingues de la rubrique Prix & produits.
  const [langueMat, setLangueMat] = useState(fr ? "fr" : "en");
  const [langueProd, setLangueProd] = useState(fr ? "fr" : "en");
  const [paiementEnCours, setPaiementEnCours] = useState("");

  // Paire de langues de travail du compte : FR/EN au Canada, EN/ES aux
  // États-Unis. Tout ce qui est bilingue à l'écran se limite à cette paire.
  const langues = paireLangues(settings?.country);

  useEffect(() => {
    api.get("/settings").then(({ data }) => setSettings(data));
    api.get("/subscription").then(({ data }) => setAbo(data)).catch(() => {});
    api.get("/plans").then(({ data }) => setPlansDispo(data.plans || [])).catch(() => {});
  }, []);

  // Changer de pays change la paire de langues : une langue d'édition qui
  // n'en fait plus partie retombe sur la première langue de la nouvelle paire.
  useEffect(() => {
    if (!settings) return;
    const paire = paireLangues(settings.country);
    setLangueMat((l) => (paire.includes(l) ? l : paire[0]));
    setLangueProd((l) => (paire.includes(l) ? l : paire[0]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings?.country]);

  // Lance le paiement Stripe pour un forfait.
  const payer = async (planId) => {
    setPaiementEnCours(planId);
    try {
      const { data } = await api.post("/stripe/checkout", { plan: planId, interval: intervalle });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || (fr ? "Paiement indisponible" : "Payment unavailable"));
      setPaiementEnCours("");
    }
  };

  const ouvrirPortail = async () => {
    try {
      const { data } = await api.post("/stripe/portal");
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || (fr ? "Portail indisponible" : "Portal unavailable"));
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...settings,
        // Après un changement de pays, une langue de soumission qui ne fait
        // plus partie de la paire retombe sur la première langue de la paire.
        // On corrige au moment d'enregistrer seulement : rien n'est écrasé
        // tant que le couvreur n'a pas cliqué « Enregistrer ».
        quote_language: paireLangues(settings.country).includes(settings.quote_language)
          ? settings.quote_language
          : paireLangues(settings.country)[0],
        labor_rate_per_square: Number(settings.labor_rate_per_square),
        waste_percent: Number(settings.waste_percent),
        tax_percent: Number(settings.tax_percent),
        labor_cost_per_square: Number(settings.labor_cost_per_square) || 0,
        flat_waste_percent: Number(settings.flat_waste_percent) || 0,
        flat_labor_rate_per_square: Number(settings.flat_labor_rate_per_square) || 0,
        flat_labor_cost_per_square: Number(settings.flat_labor_cost_per_square) || 0,
        // Le serveur revalide, mais on évite d'enregistrer une saisie à moitié
        // tapée qui reviendrait silencieusement à l'orange.
        widget_couleur: /^#[0-9a-fA-F]{6}$/.test(settings.widget_couleur || "")
          ? settings.widget_couleur.toUpperCase()
          : "#EA580C",
        widget_theme: settings.widget_theme || "clair",
        // Même précaution pour le modèle de soumission.
        quote_couleur: /^#[0-9a-fA-F]{6}$/.test(settings.quote_couleur || "")
          ? settings.quote_couleur.toUpperCase()
          : "#EA580C",
        quote_police: ["helvetica", "times", "courier"].includes(settings.quote_police) ? settings.quote_police : "helvetica",
        profit_value: Number(settings.profit_value) || 0,
        relance_delai_jours: Math.max(1, Number(settings.relance_delai_jours) || 3),
        relance_max: Math.max(1, Number(settings.relance_max) || 2),
        relances: (settings.relances || []).slice(0, 5).map((e) => ({
          delai_jours: Math.max(1, Number(e.delai_jours) || 3),
          objet_fr: e.objet_fr || "",
          message_fr: e.message_fr || "",
          objet_en: e.objet_en || "",
          message_en: e.message_en || "",
          objet_es: e.objet_es || "",
          message_es: e.message_es || "",
          // Anciens champs mono-langue : conservés tels quels, le serveur s'en
          // sert de repli pour les séquences écrites avant le bilingue.
          objet: e.objet || "",
          message: e.message || "",
          active: e.active !== false,
        })),
        materials: settings.materials.map((m) => ({
          name_fr: m.name_fr ?? m.name ?? "",
          name_en: m.name_en ?? m.name ?? "",
          name_es: m.name_es ?? "",
          price_per_square: Number(m.price_per_square),
          cost_per_square: Number(m.cost_per_square) || 0,
          toit: m.toit || "tous",
        })),
        choix: (settings.choix || []).map((g) => ({
          nom_fr: g.nom_fr ?? g.nom ?? "",
          nom_en: g.nom_en ?? g.nom ?? "",
          nom_es: g.nom_es ?? "",
          options: (g.options || []).map((o) => ({
            label_fr: o.label_fr ?? o.label ?? "",
            label_en: o.label_en ?? o.label ?? "",
            label_es: o.label_es ?? "",
            price_per_square: Number(o.price_per_square) || 0,
            cost_per_square: Number(o.cost_per_square) || 0,
            toit: o.toit || "tous",
            // Saisies séparées par des virgules dans l'interface, stockées en
            // liste. L'ancienne liste unilingue est conservée telle quelle :
            // elle sert de repli tant qu'une langue n'a pas la sienne.
            couleurs: listeCouleurs(o.couleurs),
            couleurs_fr: listeCouleurs(o.couleurs_fr ?? o.couleurs),
            couleurs_en: listeCouleurs(o.couleurs_en ?? o.couleurs),
            couleurs_es: listeCouleurs(o.couleurs_es ?? o.couleurs),
          })),
        })),
      };
      await api.put("/settings", payload);
      toast.success(t("settings.saved"));
    } catch {
      toast.error(t("settings.saveFail"));
    } finally {
      setSaving(false);
    }
  };

  // ---- Séquence de suivis (5 au maximum) ----
  const etapesRelance = settings?.relances?.length ? settings.relances : [{ ...ETAPE_VIDE, delai_jours: 3 }];
  const majEtapes = (liste) => setSettings({ ...settings, relances: liste });
  const majEtape = (i, cle, val) =>
    majEtapes(etapesRelance.map((e, j) => (j === i ? { ...e, [cle]: val } : e)));
  const ajouterEtape = () => majEtapes([...etapesRelance, { ...ETAPE_VIDE }]);
  const retirerEtape = (i) => majEtapes(etapesRelance.filter((_, j) => j !== i));

  const updateMaterial = (i, key, val) => {
    const materials = [...settings.materials];
    materials[i] = { ...materials[i], [key]: val };
    setSettings({ ...settings, materials });
  };
  const addMaterial = () => setSettings({ ...settings, materials: [...settings.materials, { name_fr: "Nouveau poste", name_en: "New item", price_per_square: 0, cost_per_square: 0, toit: "tous" }] });
  const removeMaterial = (i) => setSettings({ ...settings, materials: settings.materials.filter((_, idx) => idx !== i) });

  // ---- Groupes de choix (produits sélectionnés propriété par propriété) ----
  const groupes = settings?.choix || [];
  const majGroupes = (g) => setSettings({ ...settings, choix: g });
  const ajouterGroupe = () =>
    majGroupes([...groupes, { nom_fr: "Nouveau poste", nom_en: "New item", options: [] }]);
  const retirerGroupe = (gi) => majGroupes(groupes.filter((_, i) => i !== gi));
  const majGroupe = (gi, cle, val) =>
    majGroupes(groupes.map((g, i) => (i === gi ? { ...g, [cle]: val } : g)));
  const ajouterOption = (gi) =>
    majGroupes(groupes.map((g, i) =>
      i === gi
        ? { ...g, options: [...(g.options || []), { label_fr: "Nouveau produit", label_en: "New product", price_per_square: 0, cost_per_square: 0, couleurs: [], toit: "tous" }] }
        : g));
  const majOption = (gi, oi, cle, val) =>
    majGroupes(groupes.map((g, i) =>
      i === gi
        ? { ...g, options: g.options.map((o, j) => (j === oi ? { ...o, [cle]: val } : o)) }
        : g));
  const retirerOption = (gi, oi) =>
    majGroupes(groupes.map((g, i) =>
      i === gi ? { ...g, options: g.options.filter((_, j) => j !== oi) } : g));

  const onLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 800 * 1024) { toast.error("Logo > 800KB"); return; }
    const reader = new FileReader();
    reader.onload = () => setSettings({ ...settings, logo_base64: reader.result });
    reader.readAsDataURL(file);
  };

  // ---- Widget : activation, code d'intégration, test ----
  const basculerWidget = async () => {
    try {
      const { data } = await api.put("/widget/toggle");
      setAbo((a) => ({ ...a, widget_actif: data.widget_actif, public_token: data.public_token }));
      toast.success(data.widget_actif
        ? (fr ? "Estimateur activé" : "Estimator enabled")
        : (fr ? "Estimateur désactivé" : "Estimator disabled"));
    } catch {
      toast.error(fr ? "Échec" : "Failed");
    }
  };

  // Avec une langue explicite, le div porte data-lang ; sans, le widget suit
  // la langue de soumission du compte (comportement historique).
  const codeIntegration = abo?.public_token
    ? `<div id="estimateur-toiture" data-token="${abo.public_token}"${langueWidget ? ` data-lang="${langueWidget}"` : ""}></div>\n<script src="${BACKEND_ORIGIN}/api/widget.js"></script>`
    : "";

  const copierCode = () => {
    navigator.clipboard.writeText(codeIntegration).then(() => {
      setCopie(true);
      toast.success(fr ? "Code copié" : "Code copied");
      setTimeout(() => setCopie(false), 2000);
    });
  };

  // La page de test répercute le choix de langue du code d'intégration.
  const testerWidget = () => {
    window.open(
      `${BACKEND_ORIGIN}/api/public/widget-demo?token=${abo.public_token}&theme=${settings?.widget_theme || "clair"}${langueWidget ? `&lang=${langueWidget}` : ""}`,
      "widget-test",
      "width=560,height=760,resizable=yes,scrollbars=yes"
    );
  };

  if (!settings) return <Layout><div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-brand-ink" /></div></Layout>;

  // Habillage du widget : couleur validée et palette de l'aperçu. En mode
  // « auto » on suit la préférence du navigateur, exactement comme le widget.
  const couleurWidget = /^#[0-9a-fA-F]{6}$/.test(settings.widget_couleur || "")
    ? settings.widget_couleur.toUpperCase()
    : "#EA580C";
  const apercuSombre = settings.widget_theme === "sombre"
    || (settings.widget_theme === "auto" && typeof window !== "undefined"
        && window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches);
  const P = paletteWidget(apercuSombre);

  const field = "mt-1 bg-field border-line-strong rounded-md focus:border-orange-500 focus-visible:ring-orange-500";
  const carte = "bg-surface border border-line rounded-xl p-6 shadow-carte mb-6";
  const selectNatif = "w-full mt-1 bg-field border border-line-strong focus:border-orange-500 rounded-md px-3 py-2 text-sm text-ink outline-none";

  // Ce que chaque palier apporte DE PLUS que celui d'en dessous, dans la
  // rubrique Abonnement. Volontairement incrémental : la liste complète vit sur
  // la page Forfaits publique, ici le couvreur veut savoir ce qu'il gagne à
  // monter d'un cran. À garder synchronisé avec `inclus()` de pages/Tarifs.js
  // et avec PLANS_RELANCE_AUTO / PLANS_VENDEURS côté serveur.
  const plusQueLePrecedent = (id) => {
    const T = {
      fr: {
        trio: ["Relance automatique des prospects", "Séquence de 5 suivis programmés, à vos délais"],
        team: ["Vendeurs assignés, imprimés sur vos soumissions"],
        legend: ["Dépassement au tarif le plus bas"],
      },
      en: {
        trio: ["Automatic lead follow-up", "5-step follow-up sequence, on your schedule"],
        team: ["Salespeople assigned and printed on your quotes"],
        legend: ["Lowest overage rate"],
      },
      es: {
        trio: ["Seguimiento automático de prospectos", "Secuencia de 5 seguimientos programados, a sus tiempos"],
        team: ["Vendedores asignados, impresos en sus cotizaciones"],
        legend: ["La tarifa de excedente más baja"],
      },
    };
    const L = T[lang] || T.en;
    // Depuis le 2026-09-24, la relance automatique est DÉJÀ dans Solo : les
    // deux points de `L.trio` ne sont donc plus un gain en montant d'un cran,
    // ils font partie du socle. Team n'ajoute plus que les vendeurs assignés.
    if (id === "team") return L.team;
    if (id === "legend") return [...L.team, ...L.legend];
    return [];   // Solo (et Trio, retiré de la vente) : le socle.
  };

  const TITRES = {
    entreprise: fr ? "Entreprise" : "Company",
    prix: fr ? "Prix & produits" : "Pricing & products",
    soumission: fr ? "Modèle de soumission" : "Quote template",
    widget: "Widget",
    relance: fr ? "Relance" : "Follow-up",
    abonnement: fr ? "Abonnement" : "Subscription",
  };

  return (
    <Layout>
      <div className="p-4 sm:p-6 md:p-10 max-w-6xl">
        <div className="flex items-end justify-between gap-4 mb-8">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-ink mb-2">{t("settings.overline")}</p>
            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tighter">{TITRES[onglet]}</h1>
          </div>
          {onglet !== "abonnement" && (
            <Button onClick={save} disabled={saving} data-testid="save-settings-button" className="bg-orange-600 hover:bg-orange-700 rounded-md font-semibold">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-2" /> {t("settings.save")}</>}
            </Button>
          )}
        </div>

        {/* ============================== ENTREPRISE ============================== */}
        {onglet === "entreprise" && (
          <>
            <div className={carte}>
              <h3 className="font-heading font-bold mb-4">{t("settings.companyDetails")} <span className="text-xs font-normal text-ink-faint">{t("settings.shownPdf")}</span></h3>
              <div className="flex items-start gap-5 mb-5">
                <div className="h-24 w-24 shrink-0 bg-field border border-line-strong rounded-md flex items-center justify-center overflow-hidden">
                  {settings.logo_base64 ? (
                    <img src={settings.logo_base64} alt="Logo" className="max-h-full max-w-full object-contain" data-testid="logo-preview" />
                  ) : (
                    <ImageIcon className="h-8 w-8 text-ink-faint" />
                  )}
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-ink-soft">{t("settings.companyLogo")}</Label>
                  <div className="flex items-center gap-2 mt-2">
                    <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 text-sm bg-hover border border-line-strong rounded-md hover:bg-hover-strong transition-colors">
                      <Upload className="h-4 w-4" /> {t("settings.upload")}
                      <input type="file" accept="image/*" onChange={onLogoChange} data-testid="logo-upload-input" className="hidden" />
                    </label>
                    {settings.logo_base64 && (
                      <button data-testid="remove-logo-button" onClick={() => setSettings({ ...settings, logo_base64: "" })} className="text-sm text-ink-soft hover:text-red-400 transition-colors">{t("settings.remove")}</button>
                    )}
                  </div>
                  <p className="text-xs text-ink-faint mt-2">{t("settings.logoHint")}</p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-ink-soft">{t("settings.companyName")}</Label>
                  <Input data-testid="company-name-input" value={settings.company_name || ""} onChange={(e) => setSettings({ ...settings, company_name: e.target.value })} className={field} />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-ink-soft">{t("settings.phone")}</Label>
                  <Input data-testid="company-phone-input" value={settings.company_phone || ""} onChange={(e) => setSettings({ ...settings, company_phone: e.target.value })} className={field} />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-ink-soft">{t("settings.website")}</Label>
                  <Input data-testid="company-website-input" value={settings.company_website || ""} onChange={(e) => setSettings({ ...settings, company_website: e.target.value })} className={field} />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-ink-soft">{fr ? "Courriel affiché (PDF)" : "Public email (PDF)"}</Label>
                  <Input data-testid="company-email-input" value={settings.company_email || ""} onChange={(e) => setSettings({ ...settings, company_email: e.target.value })} className={field} />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs uppercase tracking-widest text-ink-soft">{t("settings.bizAddress")}</Label>
                  <Input data-testid="company-address-input" value={settings.company_address || ""} onChange={(e) => setSettings({ ...settings, company_address: e.target.value })} className={field} />
                </div>
                {/* Licence d'entrepreneur : RBQ au Québec, license/CCB ailleurs.
                    Saisie AVEC son préfixe — elle sort telle quelle sur le PDF. */}
                <div className="sm:col-span-2">
                  <Label className="text-xs uppercase tracking-widest text-ink-soft">
                    {fr ? "Numéro de licence (RBQ au Québec)" : "License number (RBQ in Quebec)"}
                  </Label>
                  <Input
                    data-testid="company-license-input"
                    value={settings.company_license || ""}
                    onChange={(e) => setSettings({ ...settings, company_license: e.target.value })}
                    placeholder={fr ? "RBQ 5678-1234-01" : "License #123456"}
                    className={field}
                  />
                  <p className="text-xs text-ink-faint mt-1">
                    {fr
                      ? "Apparaît dans l'en-tête de vos soumissions PDF. Entrez-le avec son préfixe (RBQ, licence, permis…)."
                      : "Shown in the header of your PDF quotes. Enter it with its prefix (RBQ, license, CCB…)."}
                  </p>
                </div>
              </div>
            </div>

            <div className={carte}>
              <h3 className="font-heading font-bold mb-4">{fr ? "Courriels" : "Emails"}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-ink-soft">{fr ? "Courriel pour soumissions" : "Quotes email"}</Label>
                  <Input data-testid="notify-email-input" type="email" value={settings.notify_email || ""} onChange={(e) => setSettings({ ...settings, notify_email: e.target.value })} className={field} />
                  <p className="text-xs text-ink-faint mt-1">{fr ? "Reçoit chaque demande du widget, prête à valider. Vide = courriel du compte." : "Receives each widget request, ready to validate. Empty = account email."}</p>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-ink-soft">{fr ? "Courriel administratif" : "Administrative email"}</Label>
                  <Input data-testid="admin-email-input" type="email" value={settings.admin_email || ""} onChange={(e) => setSettings({ ...settings, admin_email: e.target.value })} className={field} />
                  <p className="text-xs text-ink-faint mt-1">{fr ? "Facturation et expéditeur des courriels envoyés à vos clients." : "Billing and sender of emails sent to your clients."}</p>
                </div>
              </div>
            </div>

            <div className={carte}>
              <h3 className="font-heading font-bold mb-4">{fr ? "Région & taxes" : "Region & taxes"}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-ink-soft">{t("settings.addrCountry")}</Label>
                  <Select value={settings.country || "ca"} onValueChange={(v) => setSettings({ ...settings, country: v })}>
                    <SelectTrigger data-testid="country-select" className={field}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-surface border-line-strong text-ink">
                      <SelectItem value="ca" className="focus:bg-orange-600 focus:text-white">{t("settings.canada")}</SelectItem>
                      <SelectItem value="us" className="focus:bg-orange-600 focus:text-white">{t("settings.usa")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-ink-soft">{t("settings.tax")}</Label>
                  <Input data-testid="tax-input" type="number" placeholder={EX.taxe} value={ex0(settings.tax_percent)} onChange={(e) => setSettings({ ...settings, tax_percent: e.target.value })} className={field} />
                </div>
              </div>
            </div>

            <CarteMotDePasse className={carte} />
          </>
        )}

        {/* ============================== MODÈLE DE SOUMISSION ==============================
            Tout ce qui décrit le DOCUMENT remis au client vit ici : langue et
            style par défaut, politique imprimée au bas, couleur d'accent et
            police du PDF. Les coordonnées (nom, licence, logo) restent dans
            Entreprise — elles servent aussi ailleurs. */}
        {onglet === "soumission" && (
          <>
            <div className={carte}>
              <h3 className="font-heading font-bold mb-4">{fr ? "Contenu par défaut" : "Defaults"}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-ink-soft">{t("settings.quoteLangLabel")}</Label>
                  {/* Les langues offertes suivent le pays du compte :
                      FR/EN au Canada, EN/ES aux États-Unis. */}
                  <Select value={settings.quote_language || langues[0]} onValueChange={(v) => setSettings({ ...settings, quote_language: v })}>
                    <SelectTrigger data-testid="quote-language-select" className={field}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-surface border-line-strong text-ink">
                      {langues.map((code) => (
                        <SelectItem key={code} value={code} className="focus:bg-orange-600 focus:text-white">{NOMS_LANGUES[code]}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-ink-faint mt-1">{t("settings.quoteLangHint")}</p>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-ink-soft">{t("settings.quoteStyleDefault")}</Label>
                  <select data-testid="quote-style-default-select"
                    value={settings.quote_style === "forfaitaire" ? "forfaitaire" : "detaillee"}
                    onChange={(e) => setSettings({ ...settings, quote_style: e.target.value })}
                    className={selectNatif}>
                    <option value="detaillee">{t("settings.styleDetailed")}</option>
                    <option value="forfaitaire">{t("settings.styleLump")}</option>
                  </select>
                  <p className="text-xs text-ink-faint mt-1">{t("settings.styleHint")}</p>
                </div>
              </div>
            </div>

            <div className={carte}>
              <h3 className="font-heading font-bold mb-4">
                {fr ? "Politique de soumission" : "Quote policy"}{" "}
                <span className="text-xs font-normal text-ink-faint">{fr ? "(facultatif)" : "(optional)"}</span>
              </h3>
              {/* Texte libre imprimé au bas de chaque PDF sous « Conditions ».
                  Chaque ligne devient un paragraphe. */}
              <textarea
                data-testid="company-policy-input"
                value={settings.company_policy || ""}
                onChange={(e) => setSettings({ ...settings, company_policy: e.target.value })}
                maxLength={4000}
                rows={5}
                placeholder={fr
                  ? "Ex. : Cette soumission est valide 30 jours.\nUn acompte de 20 % est demandé à la signature."
                  : "E.g.: This quote is valid for 30 days.\nA 20% deposit is required upon signing."}
                className="w-full bg-field border border-line-strong rounded-md px-3 py-2 text-sm focus:border-orange-500 focus:outline-none focus:ring-1 focus:ring-orange-500 placeholder:text-ink-faint"
              />
              <p className="text-xs text-ink-faint mt-1">
                {fr
                  ? "Imprimée au bas de chaque soumission PDF, sous le titre « Conditions » : validité du prix, acompte, garanties, ou tout autre message utile."
                  : "Printed at the bottom of every PDF quote under \"Terms & conditions\": price validity, deposit, warranties, or any other useful message."}
              </p>
            </div>

            <div className={carte}>
              <h3 className="font-heading font-bold mb-4">
                {fr ? "Vendeurs" : "Salespeople"}{" "}
                <span className="text-xs font-normal text-ink-faint">
                  {fr ? `(forfait ${abo?.vendeurs_plan || "Team"} et plus)` : `(${abo?.vendeurs_plan || "Team"} plan and up)`}
                </span>
              </h3>
              {/* Liste nom + numéro direct. À la création d'une soumission, un
                  menu déroulant permet d'assigner un vendeur : il remplace le
                  titulaire dans « Préparé par » sur le PDF client. Pas de
                  comptes ni de mots de passe pour l'instant (décision de Jeff). */}
              {abo && abo.vendeurs_permis === false ? (
                <p className="text-sm text-ink-soft" data-testid="vendeurs-verrou">
                  {fr
                    ? `Réservé aux forfaits ${abo.vendeurs_plan || "Team"} et plus : chaque soumission peut alors être assignée à un vendeur, dont le nom et le numéro direct apparaissent sur le PDF du client.`
                    : `Available on the ${abo.vendeurs_plan || "Team"} plan and up: each quote can then be assigned to a salesperson, whose name and direct number appear on the client PDF.`}
                </p>
              ) : (
                <>
                  {(settings.vendeurs || []).map((v, i) => (
                    <div key={i} className="flex items-center gap-2 mb-2">
                      <Input
                        data-testid={`vendeur-nom-${i}`}
                        value={v.nom || ""}
                        placeholder={fr ? "Nom du vendeur" : "Salesperson name"}
                        onChange={(e) => {
                          const liste = [...(settings.vendeurs || [])];
                          liste[i] = { ...liste[i], nom: e.target.value };
                          setSettings({ ...settings, vendeurs: liste });
                        }}
                        className="flex-1 bg-field border-line-strong rounded-md"
                      />
                      <Input
                        data-testid={`vendeur-tel-${i}`}
                        value={v.telephone || ""}
                        placeholder={fr ? "Numéro direct" : "Direct number"}
                        onChange={(e) => {
                          const liste = [...(settings.vendeurs || [])];
                          liste[i] = { ...liste[i], telephone: e.target.value };
                          setSettings({ ...settings, vendeurs: liste });
                        }}
                        className="w-44 bg-field border-line-strong rounded-md"
                      />
                      <button
                        data-testid={`vendeur-supprimer-${i}`}
                        onClick={() => setSettings({ ...settings, vendeurs: (settings.vendeurs || []).filter((_, j) => j !== i) })}
                        className="p-2 text-ink-soft hover:text-red-400 rounded-md hover:bg-hover-strong transition-colors"
                        title={fr ? "Retirer" : "Remove"}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  <Button
                    variant="outline"
                    data-testid="vendeur-ajouter"
                    onClick={() => setSettings({ ...settings, vendeurs: [...(settings.vendeurs || []), { nom: "", telephone: "" }] })}
                    className="mt-1 border-line-strong rounded-md hover:bg-hover-strong hover:text-ink"
                  >
                    <Plus className="h-4 w-4 mr-2" /> {fr ? "Ajouter un vendeur" : "Add a salesperson"}
                  </Button>
                  <p className="text-xs text-ink-faint mt-3">
                    {fr
                      ? "À la création d'une soumission, un menu permet d'assigner un vendeur : son nom et son numéro remplacent le vôtre dans « Préparé par » sur le PDF. Identifiants individuels : à venir."
                      : "When creating a quote, a menu lets you assign a salesperson: their name and number replace yours in \"Prepared by\" on the PDF. Individual logins: coming later."}
                  </p>
                </>
              )}
            </div>

            <div className={carte}>
              <h3 className="font-heading font-bold mb-4">{fr ? "Apparence du PDF" : "PDF appearance"}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-ink-soft">{fr ? "Couleur d'accent" : "Accent color"}</Label>
                  {/* Même palette de départ que le widget ; le serveur revalide
                      le hexadécimal au moment de générer le PDF. */}
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    {["#EA580C", "#DC2626", "#D97706", "#16A34A", "#0D9488", "#2563EB", "#7C3AED", "#DB2777", "#475569", "#171717"].map((c) => (
                      <button
                        key={c}
                        type="button"
                        data-testid={`quote-couleur-${c.slice(1)}`}
                        onClick={() => setSettings({ ...settings, quote_couleur: c })}
                        className={`h-7 w-7 rounded-full border-2 transition-transform hover:scale-110 ${
                          (settings.quote_couleur || "#EA580C").toUpperCase() === c ? "border-ink" : "border-transparent"
                        }`}
                        style={{ backgroundColor: c }}
                        aria-label={c}
                      />
                    ))}
                    <input
                      type="color"
                      data-testid="quote-couleur-libre"
                      value={/^#[0-9a-fA-F]{6}$/.test(settings.quote_couleur || "") ? settings.quote_couleur : "#EA580C"}
                      onChange={(e) => setSettings({ ...settings, quote_couleur: e.target.value.toUpperCase() })}
                      className="h-7 w-9 rounded cursor-pointer border border-line-strong bg-field"
                      title={fr ? "Couleur libre" : "Custom color"}
                    />
                    <Input
                      data-testid="quote-couleur-hex"
                      value={settings.quote_couleur || "#EA580C"}
                      onChange={(e) => setSettings({ ...settings, quote_couleur: e.target.value })}
                      className="w-28 bg-field border-line-strong rounded-md"
                    />
                  </div>
                  <p className="text-xs text-ink-faint mt-2">
                    {fr ? "Titres de section et ligne TOTAL du PDF." : "Section headings and the TOTAL row of the PDF."}
                  </p>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-ink-soft">{fr ? "Police du PDF" : "PDF font"}</Label>
                  <select
                    data-testid="quote-police-select"
                    value={settings.quote_police || "helvetica"}
                    onChange={(e) => setSettings({ ...settings, quote_police: e.target.value })}
                    className={selectNatif}
                  >
                    <option value="helvetica">{fr ? "Helvetica — moderne (défaut)" : "Helvetica — modern (default)"}</option>
                    <option value="times">{fr ? "Times — classique" : "Times — classic"}</option>
                    <option value="courier">{fr ? "Courier — machine à écrire" : "Courier — typewriter"}</option>
                  </select>
                  {/* Aperçu approximatif : les polices écran équivalentes. */}
                  <p
                    className="text-sm mt-2 px-3 py-2 bg-field border border-line rounded-md"
                    style={{
                      fontFamily: { times: "Georgia, 'Times New Roman', serif", courier: "'Courier New', monospace" }[settings.quote_police] || "Helvetica, Arial, sans-serif",
                    }}
                  >
                    {fr ? "Aperçu : Soumission de toiture — 12 345,67 $" : "Preview: Roofing quote — $12,345.67"}
                  </p>
                </div>
              </div>
            </div>
          </>
        )}

        {/* ============================== PRIX & PRODUITS ============================== */}
        {onglet === "prix" && (
          <>
            {/* C'est ici qu'on répare le problème : le bandeau reste tant qu'aucun
                montant n'est saisi, et disparaît dès le premier prix entré. */}
            <AlertePrix settings={settings} avecLien={false} className="mb-6" />
            <div className={carte}>
              <h3 className="font-heading font-bold mb-4">{t("settings.laborAdj")}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-ink-soft">{t("settings.laborPerSq")}</Label>
                  <Input data-testid="labor-rate-input" type="number" placeholder={EX.mo} value={ex0(settings.labor_rate_per_square)} onChange={(e) => setSettings({ ...settings, labor_rate_per_square: e.target.value })} className={field} />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-ink-soft">{t("settings.laborCostPerSq")}</Label>
                  <Input data-testid="labor-cost-input" type="number" placeholder={t("settings.costPh")} value={ex0(settings.labor_cost_per_square)} onChange={(e) => setSettings({ ...settings, labor_cost_per_square: e.target.value })} className={field} />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-ink-soft">{t("settings.waste")}</Label>
                  <Input data-testid="waste-input" type="number" value={settings.waste_percent} onChange={(e) => setSettings({ ...settings, waste_percent: e.target.value })} className={field} />
                </div>
              </div>
            </div>

            {/* Toit plat : membrane en rouleaux, donc bien moins de pertes qu'un
                bardeau, et une main-d'œuvre qui n'a rien à voir. */}
            <div className={carte}>
              <h3 className="font-heading font-bold mb-1">{fr ? "Toit plat" : "Flat roof"}</h3>
              <p className="text-xs text-ink-faint mb-4 max-w-xl">
                {fr
                  ? "Utilisé automatiquement dès que la pente du bâtiment principal est « Flat (0/12) ». Laissez un taux de main-d'œuvre à 0 pour garder le taux ci-dessus."
                  : "Used automatically as soon as the main building's pitch is \"Flat (0/12)\". Leave a labor rate at 0 to keep the rate above."}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-ink-soft">{fr ? "Main-d'œuvre / carré" : "Labor / square"}</Label>
                  <Input data-testid="flat-labor-rate-input" type="number" placeholder={EX.moPlat} value={ex0(settings.flat_labor_rate_per_square)} onChange={(e) => setSettings({ ...settings, flat_labor_rate_per_square: e.target.value })} className={field} />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-ink-soft">{fr ? "Coûtant main-d'œuvre" : "Labor cost"}</Label>
                  <Input data-testid="flat-labor-cost-input" type="number" placeholder={t("settings.costPh")} value={ex0(settings.flat_labor_cost_per_square)} onChange={(e) => setSettings({ ...settings, flat_labor_cost_per_square: e.target.value })} className={field} />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-ink-soft">{fr ? "Pertes (%)" : "Waste (%)"}</Label>
                  <Input data-testid="flat-waste-input" type="number" value={settings.flat_waste_percent ?? 7} onChange={(e) => setSettings({ ...settings, flat_waste_percent: e.target.value })} className={field} />
                </div>
              </div>
            </div>

            <div className={carte}>
              <h2 className="font-heading font-bold text-lg mb-1">{t("settings.profitSection")}</h2>
              <p className="text-xs text-ink-faint mb-4">{t("settings.profitHint")}</p>
              <div className="grid sm:grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-ink-soft">{t("settings.pricingMode")}</Label>
                  <select data-testid="pricing-mode-select"
                    value={settings.pricing_mode === "cout_profit" ? "cout_profit" : "vente"}
                    onChange={(e) => setSettings({ ...settings, pricing_mode: e.target.value })}
                    className={selectNatif}>
                    <option value="vente">{t("settings.modeVente")}</option>
                    <option value="cout_profit">{t("settings.modeCoutProfit")}</option>
                  </select>
                </div>
                {settings.pricing_mode === "cout_profit" && (
                  <>
                    <div>
                      <Label className="text-xs uppercase tracking-widest text-ink-soft">{t("settings.profitType")}</Label>
                      <select data-testid="profit-type-select"
                        value={settings.profit_type === "montant" ? "montant" : "pourcent"}
                        onChange={(e) => setSettings({ ...settings, profit_type: e.target.value })}
                        className={selectNatif}>
                        <option value="pourcent">{t("settings.profitPct")}</option>
                        <option value="montant">{t("settings.profitAmt")}</option>
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-widest text-ink-soft">
                        {settings.profit_type === "montant" ? t("settings.profitValueAmt") : t("settings.profitValuePct")}
                      </Label>
                      <Input data-testid="profit-value-input" type="number" value={settings.profit_value ?? 0}
                        onChange={(e) => setSettings({ ...settings, profit_value: e.target.value })} className={field} />
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className={carte}>
              <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
                <div><h3 className="font-heading font-bold">{t("settings.materialList")}</h3><p className="text-xs text-ink-faint mt-0.5"><span className="text-emerald-400">$</span> {t("settings.costTip")} &middot; <span className="text-brand-ink">$</span> {t("settings.priceTip")}</p></div>
                <div className="flex items-center gap-3">
                  <BasculeLangue valeur={langueMat} onChange={setLangueMat} fr={fr} langues={langues} testId="bascule-langue-materiaux" />
                  <Button variant="outline" onClick={addMaterial} data-testid="add-material-button" className="border-line-strong rounded-md hover:bg-hover-strong hover:text-ink">
                    <Plus className="h-4 w-4 mr-1" /> {t("settings.addMat")}
                  </Button>
                </div>
              </div>
              <div className="space-y-3">
                {settings.materials.map((m, i) => (
                  <div key={i} className="flex flex-wrap items-center gap-x-3 gap-y-2">
                    <ChampLangue langue={langueMat.toUpperCase()} data-testid={`material-name-${langueMat}-${i}`}
                      value={m[`name_${langueMat}`] ?? m.name ?? ""}
                      placeholder={langueMat === "fr" ? (fr ? "Nom en français" : "French name")
                        : langueMat === "es" ? (fr ? "Nom en espagnol" : "Spanish name")
                        : (fr ? "Nom en anglais" : "English name")}
                      onChange={(e) => updateMaterial(i, `name_${langueMat}`, e.target.value)}
                      className="bg-field border-line-strong rounded-md focus:border-orange-500 focus-visible:ring-orange-500" />
                    <SelectToit valeur={m.toit} onChange={(v) => updateMaterial(i, "toit", v)} fr={fr} testId={`material-toit-${i}`} />
                    <div className="relative w-28" title={t("settings.costTip")}>
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-emerald-500/70 text-sm">$</span>
                      <Input data-testid={`material-cost-${i}`} type="number" placeholder={t("settings.costPh")} value={ex0(m.cost_per_square)} onChange={(e) => updateMaterial(i, "cost_per_square", e.target.value)} className="pl-6 bg-field border-emerald-500/25 rounded-md focus:border-emerald-500 focus-visible:ring-emerald-500" />
                    </div>
                    <div className="relative w-28" title={t("settings.priceTip")}>
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-faint text-sm">$</span>
                      <Input data-testid={`material-price-${i}`} type="number" placeholder={exN(EX.materiaux, i)} value={ex0(m.price_per_square)} onChange={(e) => updateMaterial(i, "price_per_square", e.target.value)} className="pl-6 bg-field border-line-strong rounded-md focus:border-orange-500 focus-visible:ring-orange-500" />
                    </div>
                    <button onClick={() => removeMaterial(i)} data-testid={`remove-material-${i}`} className="p-2 text-ink-soft hover:text-red-400 transition-colors">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Catalogue : postes dont le produit se choisit propriété par propriété. */}
            <div className={carte}>
              <div className="flex flex-wrap items-start justify-between mb-1 gap-4">
                <div>
                  <h3 className="font-heading font-bold">
                    {fr ? "Produits au choix" : "Selectable products"}
                  </h3>
                  <p className="text-sm text-ink-faint mt-1 max-w-xl">
                    {fr
                      ? "Postes dont le produit se choisit à chaque soumission — le bardeau, par exemple. Le prix retenu est celui du produit sélectionné, et il se fige dans la soumission."
                      : "Items whose product is picked per quote — shingles, for instance. The selected product's price is used and frozen into the quote."}
                  </p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <BasculeLangue valeur={langueProd} onChange={setLangueProd} fr={fr} langues={langues} testId="bascule-langue-produits" />
                  <Button variant="outline" onClick={ajouterGroupe} data-testid="add-choice-group" className="border-line-strong rounded-md hover:bg-hover-strong hover:text-ink shrink-0">
                    <Plus className="h-4 w-4 mr-1" /> {fr ? "Poste" : "Item"}
                  </Button>
                </div>
              </div>

              <div className="space-y-5 mt-5">
                {groupes.length === 0 && (
                  <p className="text-sm text-ink-faint">
                    {fr ? "Aucun poste au choix." : "No selectable items."}
                  </p>
                )}
                {groupes.map((g, gi) => (
                  <div key={gi} className="border border-line rounded-md p-4">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mb-3">
                      <ChampLangue
                        langue={langueProd.toUpperCase()}
                        data-testid={`choice-group-name-${langueProd}-${gi}`}
                        value={g[`nom_${langueProd}`] ?? g.nom ?? ""}
                        placeholder={langueProd === "fr" ? (fr ? "Poste en français" : "Item in French")
                          : langueProd === "es" ? (fr ? "Poste en espagnol" : "Item in Spanish")
                          : (fr ? "Poste en anglais" : "Item in English")}
                        onChange={(e) => majGroupe(gi, `nom_${langueProd}`, e.target.value)}
                        className="bg-field border-line-strong rounded-md focus:border-orange-500 focus-visible:ring-orange-500 font-medium"
                      />
                      <Button variant="outline" onClick={() => ajouterOption(gi)} data-testid={`add-choice-option-${gi}`} className="h-9 border-line-strong rounded-md hover:bg-hover-strong hover:text-ink">
                        <Plus className="h-4 w-4 mr-1" /> {fr ? "Produit" : "Product"}
                      </Button>
                      <button onClick={() => retirerGroupe(gi)} data-testid={`remove-choice-group-${gi}`} className="p-2 text-ink-soft hover:text-red-400 transition-colors">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>

                    <div className="space-y-2">
                      {(g.options || []).map((o, oi) => (
                        <div key={oi} className="flex flex-wrap items-center gap-x-3 gap-y-2">
                          <ChampLangue
                            langue={langueProd.toUpperCase()}
                            data-testid={`choice-option-label-${langueProd}-${gi}-${oi}`}
                            value={o[`label_${langueProd}`] ?? o.label ?? ""}
                            onChange={(e) => majOption(gi, oi, `label_${langueProd}`, e.target.value)}
                            placeholder={langueProd === "fr" ? (fr ? "Modèle en français" : "Model in French")
                              : langueProd === "es" ? (fr ? "Modèle en espagnol" : "Model in Spanish")
                              : (fr ? "Modèle en anglais" : "Model in English")}
                            className="bg-field border-line-strong rounded-md focus:border-orange-500 focus-visible:ring-orange-500"
                          />
                          {/* Les couleurs aussi suivent la bascule : l'ancienne
                              liste unilingue s'affiche tant que la langue n'a
                              pas sa propre liste, et l'édition écrit dans la
                              langue en cours. */}
                          <ChampLangue
                            langue={langueProd.toUpperCase()}
                            data-testid={`choice-option-colors-${langueProd}-${gi}-${oi}`}
                            value={texteCouleurs(o[`couleurs_${langueProd}`] ?? o.couleurs)}
                            onChange={(e) => majOption(gi, oi, `couleurs_${langueProd}`, e.target.value)}
                            placeholder={fr ? "Couleurs, séparées par des virgules" : "Colors, comma separated"}
                            className="bg-field border-line-strong rounded-md focus:border-orange-500 focus-visible:ring-orange-500"
                          />
                          <SelectToit valeur={o.toit} onChange={(v) => majOption(gi, oi, "toit", v)} fr={fr} testId={`choice-option-toit-${gi}-${oi}`} />
                          <div className="relative w-24 shrink-0" title={t("settings.costTip")}>
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-emerald-500/70 text-sm">$</span>
                            <Input
                              data-testid={`choice-option-cost-${gi}-${oi}`}
                              type="number"
                              placeholder={t("settings.costPh")}
                              value={ex0(o.cost_per_square)}
                              onChange={(e) => majOption(gi, oi, "cost_per_square", e.target.value)}
                              className="pl-6 bg-field border-emerald-500/25 rounded-md focus:border-emerald-500 focus-visible:ring-emerald-500"
                            />
                          </div>
                          <div className="relative w-24 shrink-0" title={t("settings.priceTip")}>
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-faint text-sm">$</span>
                            <Input
                              data-testid={`choice-option-price-${gi}-${oi}`}
                              type="number"
                              placeholder={exN(EX.produits, oi)}
                              value={ex0(o.price_per_square)}
                              onChange={(e) => majOption(gi, oi, "price_per_square", e.target.value)}
                              className="pl-6 bg-field border-line-strong rounded-md focus:border-orange-500 focus-visible:ring-orange-500"
                            />
                          </div>
                          <button onClick={() => retirerOption(gi, oi)} data-testid={`remove-choice-option-${gi}-${oi}`} className="p-2 text-ink-soft hover:text-red-400 transition-colors">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                      {(g.options || []).length === 0 && (
                        <p className="text-sm text-ink-faint">
                          {fr ? "Aucun produit dans ce poste." : "No product in this item."}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* ============================== WIDGET ============================== */}
        {onglet === "widget" && (
          <>
            <div className={carte}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="font-medium flex items-center gap-2">
                    {abo?.widget_actif ? (
                      <Zap className="h-4 w-4 text-green-500" />
                    ) : (
                      <ZapOff className="h-4 w-4 text-ink-faint" />
                    )}
                    {abo?.widget_actif
                      ? fr ? "Estimateur actif sur votre site" : "Estimator live on your site"
                      : fr ? "Estimateur désactivé" : "Estimator off"}
                  </div>
                  <p className="text-ink-faint text-sm mt-1 max-w-md">
                    {fr
                      ? "Chaque estimation demandée par un visiteur consomme une unité de votre forfait."
                      : "Each visitor estimate uses one unit of your plan."}
                  </p>
                </div>
                <Button
                  onClick={basculerWidget}
                  data-testid="widget-toggle-button"
                  className={abo?.widget_actif ? "bg-neutral-700 hover:bg-neutral-600" : "bg-orange-600 hover:bg-orange-700"}
                >
                  {abo?.widget_actif ? (fr ? "Désactiver" : "Disable") : (fr ? "Activer" : "Enable")}
                </Button>
              </div>

              {abo?.widget_actif && (
                <div className="mt-6">
                  {/* Langue du widget : choix d'intégration local (non persisté),
                      limité à la paire de langues du pays du compte. */}
                  <div className="mb-4">
                    <Label className="text-xs uppercase tracking-widest text-ink-soft">
                      {fr ? "Langue du widget" : "Widget language"}
                    </Label>
                    <select
                      data-testid="widget-lang-select"
                      value={langueWidget}
                      onChange={(e) => setLangueWidget(e.target.value)}
                      className={`${selectNatif} max-w-xs`}
                    >
                      <option value="">{fr ? "Langue du compte (défaut)" : "Account language (default)"}</option>
                      {langues.map((l) => (
                        <option key={l} value={l}>{NOMS_LANGUES[l]}</option>
                      ))}
                    </select>
                  </div>
                  <div className="text-xs uppercase tracking-widest text-ink-faint mb-2">
                    {fr ? "Code à coller sur votre site web" : "Paste this on your website"}
                  </div>
                  <div className="relative">
                    <pre className="bg-field border border-line rounded-md p-4 pr-14 text-xs overflow-x-auto text-ink-soft">
                      {codeIntegration}
                    </pre>
                    <button
                      onClick={copierCode}
                      data-testid="copy-widget-code"
                      className="absolute top-3 right-3 p-2 rounded bg-hover hover:bg-hover-strong transition-colors"
                      title={fr ? "Copier" : "Copy"}
                    >
                      {copie ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                    </button>
                  </div>
                  <Button
                    variant="outline"
                    onClick={testerWidget}
                    data-testid="test-widget-button"
                    className="mt-3 border-line-strong rounded-md hover:bg-hover-strong hover:text-ink"
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    {fr ? "Tester le widget" : "Test the widget"}
                  </Button>
                  <p className="text-xs text-ink-faint mt-2">
                    {fr
                      ? "Ouvre une fenêtre avec le widget exactement tel qu'il apparaîtra sur votre site."
                      : "Opens a window with the widget exactly as it will appear on your site."}
                  </p>
                </div>
              )}
            </div>

            {/* Apparence : le widget doit se fondre dans le site du couvreur. */}
            <div className={carte}>
              <h2 className="font-heading font-bold text-lg mb-1">{fr ? "Apparence" : "Appearance"}</h2>
              <p className="text-xs text-ink-faint mb-4 max-w-xl">
                {fr
                  ? "Assortissez l'estimateur aux couleurs de votre site. L'aperçu ci-dessous se met à jour tout de suite ; le bouton « Tester » ouvre la version complète."
                  : "Match the estimator to your website's colors. The preview below updates instantly; the \"Test\" button opens the full version."}
              </p>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-ink-soft">{fr ? "Thème" : "Theme"}</Label>
                  <select data-testid="widget-theme-select"
                    value={settings.widget_theme || "clair"}
                    onChange={(e) => setSettings({ ...settings, widget_theme: e.target.value })}
                    className={selectNatif}>
                    <option value="clair">{fr ? "Clair" : "Light"}</option>
                    <option value="sombre">{fr ? "Sombre" : "Dark"}</option>
                    <option value="auto">{fr ? "Automatique (suit le visiteur)" : "Automatic (follows visitor)"}</option>
                  </select>

                  <Label className="text-xs uppercase tracking-widest text-ink-soft block mt-4">{fr ? "Couleur d'accent" : "Accent color"}</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {COULEURS_WIDGET.map((c) => (
                      <button
                        key={c.hex}
                        type="button"
                        data-testid={`widget-color-${c.hex.replace("#", "")}`}
                        onClick={() => setSettings({ ...settings, widget_couleur: c.hex })}
                        title={fr ? c.fr : c.en}
                        aria-label={fr ? c.fr : c.en}
                        className={`h-8 w-8 rounded-md border-2 transition-transform hover:scale-110 ${
                          couleurWidget === c.hex ? "border-white" : "border-transparent"
                        }`}
                        style={{ backgroundColor: c.hex }}
                      />
                    ))}
                  </div>
                  <div className="flex items-center gap-2 mt-3">
                    <input
                      type="color"
                      data-testid="widget-color-picker"
                      value={couleurWidget}
                      onChange={(e) => setSettings({ ...settings, widget_couleur: e.target.value.toUpperCase() })}
                      className="h-9 w-12 bg-field border border-line-strong rounded-md cursor-pointer p-1"
                    />
                    <Input
                      data-testid="widget-color-hex"
                      value={settings.widget_couleur || ""}
                      onChange={(e) => setSettings({ ...settings, widget_couleur: e.target.value })}
                      placeholder="#EA580C"
                      className="bg-field border-line-strong rounded-md focus:border-orange-500 focus-visible:ring-orange-500 w-32 font-mono text-sm"
                    />
                    {!/^#[0-9a-fA-F]{6}$/.test(settings.widget_couleur || "") && (
                      <span className="text-xs text-amber-700">{fr ? "Format attendu : #RRGGBB" : "Expected format: #RRGGBB"}</span>
                    )}
                  </div>
                </div>

                {/* Aperçu : mêmes couleurs que celles calculées par le widget. */}
                <div>
                  <Label className="text-xs uppercase tracking-widest text-ink-soft">{fr ? "Aperçu" : "Preview"}</Label>
                  <div className="mt-2 rounded-md p-4" style={{ background: apercuSombre ? "#0F0F0F" : "#F4F4F4" }}>
                    <div className="rounded-lg p-4" style={{ background: P.fond, border: `1px solid ${P.bord}`, color: P.texte }}>
                      <div className="text-sm font-semibold">{fr ? "Estimation de toiture gratuite" : "Free roof estimate"}</div>
                      <div className="text-xs mt-0.5" style={{ color: P.doux }}>
                        {fr ? "Entrez votre adresse — nous mesurons votre toit par satellite." : "Enter your address — we measure your roof by satellite."}
                      </div>
                      <div className="mt-3 rounded px-3 py-2 text-xs" style={{ background: P.champ, border: `1px solid ${P.bordChamp}`, color: P.doux }}>
                        {fr ? "Adresse (numéro et rue)" : "Address (number and street)"}
                      </div>
                      <div className="mt-2 rounded px-3 py-2 text-xs text-center font-semibold text-ink" style={{ background: couleurWidget }}>
                        {fr ? "Obtenir mon estimation" : "Get my estimate"}
                      </div>
                      <div className="mt-3 rounded p-3" style={{ background: P.bloc }}>
                        <div className="font-bold text-lg" style={{ color: couleurWidget }}>12 480 $</div>
                        <div className="text-[11px]" style={{ color: P.doux }}>
                          {fr ? "+ ou - 15 % — à valider sur place" : "+ or - 15% — to be validated on site"}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className={carte}>
              <h2 className="font-heading font-bold text-lg mb-4">{fr ? "Comportement" : "Behavior"}</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <Label className="text-xs uppercase tracking-widest text-ink-soft">{t("settings.widgetPricing")}</Label>
                  {/* Sans prix saisis, « automatique » promettrait 0 $ à un vrai
                      client : l'option est verrouillée (et le serveur refuse de
                      toute façon d'annoncer un montant — prix_configures). */}
                  <select data-testid="widget-pricing-select"
                    value={prixConfigures(settings) && settings.widget_auto_price !== false ? "auto" : "manual"}
                    disabled={!prixConfigures(settings)}
                    onChange={(e) => setSettings({ ...settings, widget_auto_price: e.target.value === "auto" })}
                    className={`${selectNatif} disabled:opacity-60 disabled:cursor-not-allowed`}>
                    <option value="auto" disabled={!prixConfigures(settings)}>{t("settings.widgetAuto")}</option>
                    <option value="manual">{t("settings.widgetManual")}</option>
                  </select>
                  {!prixConfigures(settings) && (
                    <p className="text-xs text-amber-700 mt-1.5" data-testid="widget-pricing-verrou">
                      {fr
                        ? "Aucun montant n'est encore saisi : le widget ne peut annoncer aucun prix. Entrez vos prix dans Prix & produits pour débloquer le mode automatique."
                        : "No amounts are set yet: the widget cannot show any price. Enter your prices in Pricing & products to unlock automatic mode."}
                    </p>
                  )}
                </div>
                {/* Plafond de dépense. L'estimateur mesure toujours ; cette case
                    ne change quelque chose qu'une fois le forfait épuisé. */}
                <div className="sm:col-span-2">
                  <label className="flex items-start gap-2.5 cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid="widget-stop-quota"
                      checked={settings.widget_stop_au_quota === true}
                      onChange={(e) => setSettings({ ...settings, widget_stop_au_quota: e.target.checked })}
                      className="accent-orange-600 h-4 w-4 mt-0.5 shrink-0"
                    />
                    <span>
                      <span className="text-sm font-semibold text-ink">
                        {fr ? "Ne pas dépasser mon forfait" : "Never exceed my plan"}
                      </span>
                      <span className="block text-xs text-ink-faint mt-1 max-w-2xl">
                        {fr
                          ? "Une fois vos estimations du mois utilisées, l'estimateur cesse de mesurer les toits au lieu de facturer du dépassement. Les demandes continuent d'arriver avec l'adresse et le contact — vous ne perdez aucun prospect — et vous lancez la mesure vous-même d'un clic depuis le courriel de notification."
                          : "Once your monthly estimates are used up, the estimator stops measuring roofs instead of billing overage. Requests keep coming in with the address and contact — you lose no leads — and you launch the measurement yourself in one click from the notification email."}
                      </span>
                    </span>
                  </label>
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs uppercase tracking-widest text-ink-soft">{t("settings.widgetQuestions")}</Label>
                  <p className="text-xs text-ink-faint mt-0.5 mb-2">{t("settings.widgetQuestionsHint")}</p>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {[
                      ["widget_q_dependances", t("settings.qDep")],
                      ["widget_q_urgent", t("settings.qUrgent")],
                      ["widget_q_assurance", t("settings.qAssurance")],
                      ["widget_q_financement", t("settings.qFin")],
                      ["widget_q_delai", t("settings.qDelai")],
                    ].map(([k, lbl]) => (
                      <label key={k} className="flex items-center gap-2 text-sm text-ink-soft cursor-pointer">
                        <input type="checkbox" data-testid={`${k}-checkbox`} checked={!!settings[k]}
                          onChange={(e) => setSettings({ ...settings, [k]: e.target.checked })}
                          className="accent-orange-600 h-4 w-4" />
                        {lbl}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* ============================== RELANCE ============================== */}
        {onglet === "relance" && (
          <>
            <div className={carte}>
              <h2 className="font-heading font-bold text-lg mb-1">{fr ? "Suivi automatique" : "Automatic follow-up"}</h2>
              <p className="text-xs text-ink-faint mb-5 max-w-2xl">
                {fr
                  ? "Une séquence de courriels part automatiquement aux prospects du widget restés « en attente ». Chaque délai se compte depuis l'envoi précédent. Les prospects acceptés, refusés ou désabonnés ne sont jamais relancés."
                  : "A sequence of emails is sent automatically to widget leads still \"pending\". Each delay counts from the previous send. Accepted, refused or unsubscribed leads are never followed up."}
              </p>

              <label className="flex items-center gap-2 text-sm text-ink cursor-pointer">
                <input type="checkbox" data-testid="relance-active-checkbox" checked={!!settings.relance_active}
                  onChange={(e) => setSettings({ ...settings, relance_active: e.target.checked })}
                  className="accent-orange-600 h-4 w-4" />
                {fr ? "Activer le suivi automatique" : "Enable automatic follow-up"}
              </label>
              <p className="text-xs text-ink-faint mt-3 max-w-2xl">
                {fr
                  ? "Balises disponibles dans l'objet et le message : {nom}, {adresse}, {entreprise}, {telephone}. Un lien de désabonnement est ajouté automatiquement au bas de chaque courriel — vous êtes prévenu par courriel dès qu'un prospect s'en sert."
                  : "Tags available in the subject and message: {nom}, {adresse}, {entreprise}, {telephone}. An unsubscribe link is automatically added at the bottom of every email — you receive an email as soon as a lead uses it."}
              </p>
              <p className="text-xs text-ink-faint mt-2 max-w-2xl">
                {fr
                  ? "Chaque prospect reçoit la version écrite dans la langue où il a utilisé l'estimateur. Une colonne laissée vide = le modèle fourni dans cette langue."
                  : "Each lead receives the version written in the language they used the estimator in. A column left empty = the built-in template for that language."}
              </p>
            </div>

            {etapesRelance.map((et, i) => (
              <div key={i} className={carte}>
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div>
                    <h3 className="font-heading font-bold">
                      {fr ? `Suivi ${i + 1}` : `Follow-up ${i + 1}`}
                      {i === 0 && <span className="ml-2 text-xs font-normal text-brand-ink">{fr ? "invitation à se rencontrer" : "meeting invitation"}</span>}
                    </h3>
                    <p className="text-xs text-ink-faint mt-0.5">
                      {i === 0
                        ? (fr ? "Envoyé après la demande du prospect." : "Sent after the lead's request.")
                        : (fr ? "Envoyé après le suivi précédent." : "Sent after the previous follow-up.")}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <label className="flex items-center gap-2 text-xs text-ink-soft cursor-pointer">
                      <input type="checkbox" data-testid={`relance-active-${i}`} checked={et.active !== false}
                        onChange={(e) => majEtape(i, "active", e.target.checked)}
                        className="accent-orange-600 h-4 w-4" />
                      {fr ? "Actif" : "Active"}
                    </label>
                    {etapesRelance.length > 1 && (
                      <button onClick={() => retirerEtape(i)} data-testid={`relance-remove-${i}`}
                        className="p-1.5 text-ink-soft hover:text-red-400 transition-colors">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Texte écrit avant le passage au bilingue : encore servi en
                    repli tant que la colonne correspondante reste vide. */}
                {(et.objet || et.message) && (
                  <div className="mb-4 text-xs text-amber-700 bg-amber-500/10 border border-amber-500/30 rounded-md p-3">
                    {fr
                      ? "Un texte d'avant le mode bilingue existe pour ce suivi ; il sert de repli quand une colonne est vide. "
                      : "Text from before bilingual mode exists for this follow-up; it is used as a fallback when a column is empty. "}
                    <button
                      data-testid={`relance-migrer-${i}`}
                      onClick={() => majEtapes(etapesRelance.map((e2, j) => (j === i ? {
                        ...e2,
                        objet_fr: e2.objet_fr || e2.objet, objet_en: e2.objet_en || e2.objet,
                        message_fr: e2.message_fr || e2.message, message_en: e2.message_en || e2.message,
                        objet: "", message: "",
                      } : e2)))}
                      className="font-semibold underline hover:no-underline">
                      {fr ? "Le recopier dans les deux colonnes" : "Copy it into both columns"}
                    </button>
                  </div>
                )}

                <div className="mb-5 max-w-[10rem]">
                  <Label className="text-xs uppercase tracking-widest text-ink-soft">{fr ? "Délai (jours)" : "Delay (days)"}</Label>
                  <Input data-testid={`relance-delai-${i}`} type="number" min="1" value={et.delai_jours ?? 3}
                    onChange={(e) => majEtape(i, "delai_jours", e.target.value)} className={field} />
                </div>

                {/* Une colonne par langue, chacune coiffée d'un en-tête sans
                    ambiguïté : le prospect reçoit la version de SA langue. */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Les deux colonnes suivent la paire de langues du compte :
                      FR/EN au Canada, EN/ES aux États-Unis. */}
                  {langues.map((code) => ({
                    code,
                    nom: NOMS_LANGUES[code],
                    note: code === "fr"
                      ? (fr ? "envoyée aux prospects en français" : "sent to French-speaking leads")
                      : code === "es"
                        ? (fr ? "envoyée aux prospects hispanophones" : "sent to Spanish-speaking leads")
                        : (fr ? "envoyée aux prospects en anglais" : "sent to English-speaking leads"),
                  })).map(({ code, nom, note }) => (
                    <div key={code} className="border border-line rounded-md p-4 bg-field/30">
                      <div className="flex items-baseline gap-2 mb-3">
                        <span className="text-[10px] font-extrabold tracking-wider bg-hover border border-line rounded px-1.5 py-0.5">{code.toUpperCase()}</span>
                        <span className="font-heading font-bold text-sm">{nom}</span>
                        <span className="text-xs text-ink-faint">— {note}</span>
                      </div>
                      <Label className="text-xs uppercase tracking-widest text-ink-soft">{fr ? "Objet" : "Subject"}</Label>
                      <Input data-testid={`relance-objet-${code}-${i}`} value={et[`objet_${code}`] || ""}
                        placeholder={fr ? "Vide = objet du modèle" : "Empty = template subject"}
                        onChange={(e) => majEtape(i, `objet_${code}`, e.target.value)} className={field} />
                      <Label className="text-xs uppercase tracking-widest text-ink-soft mt-4 block">{fr ? "Message" : "Message"}</Label>
                      <textarea data-testid={`relance-message-${code}-${i}`} rows={et[`message_${code}`] ? 12 : 5}
                        value={et[`message_${code}`] || ""}
                        placeholder={fr ? "Vide = message du modèle (voir ci-dessous)" : "Empty = template message (shown below)"}
                        onChange={(e) => majEtape(i, `message_${code}`, e.target.value)}
                        className="w-full mt-1 bg-field border border-line-strong focus:border-orange-500 rounded-md px-3 py-2 text-sm text-ink outline-none resize-y" />
                      {!et[`message_${code}`] && (
                        <div className="mt-2 bg-field/60 border border-line rounded-md p-3">
                          <p className="text-[11px] uppercase tracking-widest text-ink-faint mb-1.5">{fr ? "Modèle utilisé" : "Template used"}</p>
                          <pre className="text-xs text-ink-soft whitespace-pre-wrap font-sans leading-relaxed">{MODELES_RELANCE(code)[Math.min(i, 4)]}</pre>
                          <button onClick={() => majEtape(i, `message_${code}`, MODELES_RELANCE(code)[Math.min(i, 4)])}
                            data-testid={`relance-copy-template-${code}-${i}`}
                            className="mt-2 text-xs text-brand-ink hover:text-brand-ink font-semibold">
                            {fr ? "Partir de ce modèle pour le modifier" : "Start from this template to edit it"}
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {etapesRelance.length < 5 && (
              <Button variant="outline" onClick={ajouterEtape} data-testid="relance-add-step"
                className="border-line-strong rounded-md hover:bg-hover-strong hover:text-ink mb-6">
                <Plus className="h-4 w-4 mr-1" /> {fr ? "Ajouter un suivi" : "Add a follow-up"}
              </Button>
            )}
          </>
        )}

        {/* ============================== ABONNEMENT ============================== */}
        {onglet === "abonnement" && <CarteParrainage className={carte} />}

        {onglet === "abonnement" && (
          <div className={carte}>
            <h2 className="font-heading font-bold text-lg mb-5">{fr ? "Votre abonnement" : "Your subscription"}</h2>
            {!abo ? (
              <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-brand-ink" /></div>
            ) : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                  <div className="bg-field border border-line rounded-md p-4">
                    <div className="text-xs uppercase tracking-widest text-ink-faint mb-1">{fr ? "Forfait" : "Plan"}</div>
                    <div className="font-heading font-bold text-xl text-brand-ink">{fr ? abo.plan_nom : abo.plan_nom_en}</div>
                    {abo.prix_mois > 0 && (
                      <div className="text-xs text-ink-faint mt-1">
                        {abo.prix_mois} $ / {fr ? "mois" : "month"}
                        {abo.prix_annuel_mois > 0 && (
                          <span> · {abo.prix_annuel_mois} $ {fr ? "payé à l'année" : "billed yearly"}</span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="bg-field border border-line rounded-md p-4">
                    <div className="text-xs uppercase tracking-widest text-ink-faint mb-1">{fr ? "Utilisées ce mois" : "Used this month"}</div>
                    <div className="font-heading font-bold text-xl">{abo.utilise}</div>
                    <div className="text-xs text-ink-faint mt-1">{fr ? "estimations" : "estimates"}</div>
                  </div>
                  <div className="bg-field border border-line rounded-md p-4">
                    <div className="text-xs uppercase tracking-widest text-ink-faint mb-1">{fr ? "Limite mensuelle" : "Monthly limit"}</div>
                    <div className="font-heading font-bold text-xl">{abo.limite_mensuelle === 0 ? (fr ? "Illimitée" : "Unlimited") : abo.limite_mensuelle}</div>
                  </div>
                  <div className="bg-field border border-line rounded-md p-4">
                    <div className="text-xs uppercase tracking-widest text-ink-faint mb-1">{fr ? "Restantes" : "Remaining"}</div>
                    <div className={`font-heading font-bold text-xl ${abo.restant !== null && abo.restant <= 5 ? "text-red-400" : "text-emerald-400"}`}>
                      {abo.restant === null ? "—" : abo.restant}
                    </div>
                    {abo.surplus > 0 && (
                      <div className="text-xs text-ink-faint mt-1">
                        {fr ? `puis ${abo.surplus} $ / estimation` : `then $${abo.surplus} / estimate`}
                      </div>
                    )}
                  </div>
                </div>

                {abo.depassement > 0 && (
                  <div className="mb-6 text-sm bg-amber-500/10 border border-amber-500/30 rounded-md px-4 py-3 text-amber-800">
                    {fr
                      ? `${abo.depassement} estimation(s) au-delà du forfait ce mois-ci — ${abo.facture_surplus.toLocaleString("fr-CA")} $ en surplus (${abo.surplus} $ chacune). L'outil ne s'arrête jamais : le dépassement s'ajoute à votre facture.`
                      : `${abo.depassement} estimate(s) over your plan this month — $${abo.facture_surplus.toLocaleString("en-CA")} in overage ($${abo.surplus} each). The tool never stops: overage is added to your bill.`}
                  </div>
                )}

                {abo.limite_mensuelle > 0 && (
                  <div className="mb-6">
                    <div className="flex justify-between text-xs text-ink-faint mb-1.5">
                      <span>{fr ? "Utilisation du mois" : "Monthly usage"}</span>
                      <span>{Math.min(100, Math.round((abo.utilise / abo.limite_mensuelle) * 100))} %</span>
                    </div>
                    <div className="h-2 bg-field border border-line rounded-md overflow-hidden">
                      <div
                        className={`h-full ${abo.utilise / abo.limite_mensuelle >= 0.9 ? "bg-red-500" : "bg-orange-600"}`}
                        style={{ width: `${Math.min(100, (abo.utilise / abo.limite_mensuelle) * 100)}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* À la Carte : le solde remplace le quota. On montre ce qui
                    reste, ce que coûte une mesure, et on recharge d'ici. */}
                {abo.a_la_carte && (
                  <div className="border-t border-line pt-6" data-testid="bloc-solde">
                    <h3 className="font-heading font-bold mb-3">{fr ? "Votre solde" : "Your balance"}</h3>
                    <div className="flex items-baseline gap-3 flex-wrap mb-1">
                      <span className="font-heading text-3xl font-extrabold tracking-tighter">
                        {abo.solde.toFixed(2)} {abo.devise === "usd" ? "$ US" : "$"}
                      </span>
                      {abo.mesures_offertes > 0 && (
                        <span className="text-sm font-semibold text-emerald-700" data-testid="mesures-offertes">
                          {fr
                            ? `+ ${abo.mesures_offertes} mesure(s) offerte(s)`
                            : `+ ${abo.mesures_offertes} free measurement(s)`}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-ink-faint mb-4">
                      {fr
                        ? `Chaque mesure satellite coûte ${abo.prix_mesure} $, prise sur ce solde.`
                        : `Each satellite measurement costs $${abo.prix_mesure}, taken from this balance.`}
                    </p>
                    <div className="flex items-end gap-2 flex-wrap">
                      <div>
                        <Label className="text-xs uppercase tracking-widest text-ink-soft">
                          {fr ? "Montant à ajouter" : "Amount to add"}
                        </Label>
                        <Input
                          type="number" min={abo.recharge_min} max={abo.recharge_max} step="5"
                          value={montantRecharge} data-testid="montant-recharge"
                          onChange={(e) => setMontantRecharge(e.target.value)}
                          className={`mt-1 w-32 ${field}`}
                        />
                      </div>
                      <Button
                        onClick={recharger} disabled={rechargeEnCours}
                        data-testid="bouton-recharge"
                        className="bg-orange-600 hover:bg-orange-700 rounded-md font-semibold"
                      >
                        {rechargeEnCours && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                        {fr ? "Ajouter des fonds" : "Add funds"}
                      </Button>
                      <span className="text-xs text-ink-faint pb-2">
                        {fr
                          ? `entre ${abo.recharge_min} $ et ${abo.recharge_max} $`
                          : `between $${abo.recharge_min} and $${abo.recharge_max}`}
                      </span>
                    </div>
                  </div>
                )}

                {abo.stripe ? (
                  <div className="border-t border-line pt-6">
                    <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                      <h3 className="font-heading font-bold">{fr ? "Choisir ou changer de forfait" : "Choose or change plan"}</h3>
                      <div className="flex gap-1">
                        <button onClick={() => setIntervalle("month")}
                          className={`px-3 py-1.5 rounded-md text-xs font-semibold ${intervalle === "month" ? "bg-orange-600 text-white" : "bg-hover text-ink-soft hover:text-white"}`}>
                          {fr ? "Mensuel" : "Monthly"}
                        </button>
                        <button onClick={() => setIntervalle("year")}
                          className={`px-3 py-1.5 rounded-md text-xs font-semibold ${intervalle === "year" ? "bg-orange-600 text-white" : "bg-hover text-ink-soft hover:text-white"}`}>
                          {fr ? "Annuel (rabais)" : "Yearly (discount)"}
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                      {plansDispo.map((p) => {
                        const actuel = abo.plan === p.id;
                        // La grille suit la devise du compte : un client US voit
                        // et paie les prix US.
                        const cle = intervalle === "year" ? "prix_annuel_mois" : "prix_mois";
                        const prix = abo.devise === "usd" ? (p[`${cle}_usd`] ?? p[cle]) : p[cle];
                        const surplus = abo.devise === "usd" ? (p.surplus_usd ?? p.surplus) : p.surplus;
                        return (
                          <div key={p.id} className={`border rounded-md p-4 flex flex-col ${actuel ? "border-orange-500 bg-orange-500/[0.06]" : "border-line bg-field"}`}>
                            <div className="font-heading font-bold">{fr ? p.nom_fr : p.nom_en}</div>
                            <div className="text-sm text-ink-soft mt-1">{prix} {abo.devise === "usd" ? "$ US" : "$"} / {fr ? "mois" : "mo"}{intervalle === "year" ? (fr ? " (facturé à l'année)" : " (billed yearly)") : ""}</div>
                            <div className="text-xs text-ink-faint">{p.estimates_per_month} {fr ? "estimations" : "estimates"}</div>
                            {/* Ce que ce palier apporte DE PLUS que le précédent :
                                le couvreur qui hésite doit voir ce qu'il gagne à
                                monter, pas relire la liste complète à chaque fois. */}
                            {(plusQueLePrecedent(p.id) || []).length > 0 && (
                              <ul className="mt-3 space-y-1.5" data-testid={`plan-plus-${p.id}`}>
                                {plusQueLePrecedent(p.id).map((li, i) => (
                                  <li key={i} className="flex gap-1.5 text-xs text-ink-soft leading-snug">
                                    <Plus className="h-3 w-3 text-brand-ink shrink-0 mt-0.5" />
                                    <span>{li}</span>
                                  </li>
                                ))}
                              </ul>
                            )}
                            {surplus > 0 && (
                              <div className="text-xs text-ink-faint mt-2">
                                {fr ? `Puis ${String(surplus).replace(".", ",")} $ / estimation` : `Then $${surplus} / estimate`}
                              </div>
                            )}
                            <Button size="sm" disabled={actuel || paiementEnCours === p.id} onClick={() => payer(p.id)}
                              data-testid={`checkout-${p.id}`}
                              className={`mt-3 w-full rounded-md text-xs font-semibold ${actuel ? "bg-hover-strong" : "bg-orange-600 hover:bg-orange-700"}`}>
                              {actuel ? (fr ? "Forfait actuel" : "Current plan") : paiementEnCours === p.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (fr ? "Choisir" : "Choose")}
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                    {abo.stripe_client && (
                      <Button variant="outline" onClick={ouvrirPortail} data-testid="stripe-portal-button"
                        className="border-line-strong rounded-md hover:bg-hover-strong hover:text-ink">
                        <ExternalLink className="h-4 w-4 mr-2" />
                        {fr ? "Gérer la facturation (factures, carte, annulation)" : "Manage billing (invoices, card, cancellation)"}
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="text-sm text-ink-faint bg-field border border-line rounded-md px-4 py-3">
                    {fr
                      ? "Le paiement en ligne arrive bientôt. Pour changer de forfait, écrivez-nous : quotes@roofborder.com."
                      : "Online payment is coming soon. To change plans, email us: quotes@roofborder.com."}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
